import { FC, memo, useEffect } from 'react';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';

import { NOTIFICATION_DEFAULT_DELAY, useNotificationContext } from 'application/providers/NotificationProvider';
import { BILLING_PAGE_PATH_ALIAS } from 'application/consts';
import { useAccountCompanyId } from 'application/hooks';
import { useSearchParams } from 'application/providers/RouterProvider';
import CompanyAlreadyExistsToaster from 'application/components/InteractiveErrorToaster/CompanyAlreadyExistsToaster';

import {
  AuthCurrentUserDocument,
  AuthCurrentUserQuery,
  useAuthUserGetSberReauthUriQuery,
} from 'domain/api/graphql/generated';
import { FetchPolicyKeys } from 'domain/api/graphql/types';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import IconRenderer from '@zvuk-b2b/react-uikit/ui/icons/IconRenderer';

import { TEXTS } from './texts';
import icon from './sber_id_logo_small.svg';

import styles from './index.module.scss';

const REDIRECT_URL = `${window.location.hostname}${BILLING_PAGE_PATH_ALIAS}`;

const SberIDReauth: FC = () => {
  const notification = useNotificationContext();

  const { data: userData } = useQuery<AuthCurrentUserQuery>(AuthCurrentUserDocument, {
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const sberIdWasLinked = Boolean(userData?.currentUserPureQuery?.mainCompany?.sberIdWasLinked);
  const companyId = useAccountCompanyId();

  const [searchParams, setSearchParams] = useSearchParams();

  const mode = searchParams.get('mode');
  const error = searchParams.get('error');

  useEffect(() => {
    if (mode === 'sber_id_reauth') {
      setSearchParams(undefined);

      switch (error) {
        case 'CompanyAlreadyExists': {
          notification.showErrorNotification({
            children: <CompanyAlreadyExistsToaster />,
            delay: NOTIFICATION_DEFAULT_DELAY * 12,
          });

          break;
        }
        case null: {
          notification.showInfoNotification({
            children: TEXTS.SBER_ID_REAUTH_SUCCESS_TEXT,
          });

          break;
        }
        default: {
          notification.showErrorNotification({
            children: TEXTS.SBER_ID_REAUTH_ERROR_TEXT,
          });
        }
      }
    }
  }, []);

  const { data } = useAuthUserGetSberReauthUriQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    variables: { companyId, redirectUri: REDIRECT_URL },
  });

  const handleReauth = () => {
    const link = data?.userGetSberLinkUri;

    if (link) {
      window.location.href = link;

      notification.showInfoNotification({
        children: TEXTS.SBER_ID_REAUTH_START_TEXT,
      });
    } else {
      notification.showErrorNotification({
        children: TEXTS.SBER_ID_REAUTH_ERROR_TEXT,
      });
    }
  };

  return (
    <div className={styles.container}>
      <Button
        elementId="button_sber_business_online_reauth"
        className={styles.sberIdButton}
        type={ButtonType.PLAIN}
        isLoading={false}
        isDisabled={sberIdWasLinked}
        onClick={handleReauth}>
        <IconRenderer
          iconHtml={icon}
          className={classNames(styles.icon, {
            [styles.iconDisabled]: sberIdWasLinked,
          })}
        />
        {sberIdWasLinked ? TEXTS.SBER_ID_DISABLED_TEXT : TEXTS.SBER_ID_TEXT}
      </Button>
      <div className={styles.hint}>{TEXTS.SBER_ID_HINT}</div>
    </div>
  );
};

export default memo(SberIDReauth);
